// src/services/jobsService.js
// ─────────────────────────────────────────────────────────────
//  공고(Jobs) Firestore CRUD + 실시간 구독 + 검색
// ─────────────────────────────────────────────────────────────

import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  getDoc, getDocs, onSnapshot,
  query, where, orderBy, limit, startAfter,
  serverTimestamp, increment, Timestamp,
} from "firebase/firestore";
import { db } from "./firebase";

const JOBS_COL = "jobs";
const APPLICATIONS_COL = "applications";

// ── Firestore 데이터 ↔ 앱 모델 변환 ──────────────────────────

function toModel(docSnap) {
  const d = docSnap.data();
  return {
    id:          docSnap.id,
    title:       d.title       ?? "",
    company:     d.company     ?? "",
    category:    d.category    ?? "",
    pay:         d.pay         ?? 0,
    duration:    d.duration    ?? 0,
    location:    d.location    ?? "",
    address:     d.address     ?? "",
    lat:         d.lat         ?? null,
    lng:         d.lng         ?? null,
    urgent:      d.urgent      ?? false,
    slots:       d.slots       ?? 1,
    filledSlots: d.filledSlots ?? 0,
    tags:        d.tags        ?? [],
    startTime:   d.startTime   ?? "",
    startDate:   d.startDate   ?? "",
    detail:      d.detail      ?? "",
    employerId:  d.employerId  ?? "",
    employerName:d.employerName?? "",
    status:      d.status      ?? "open",      // open | closed | cancelled
    viewCount:   d.viewCount   ?? 0,
    applyCount:  d.applyCount  ?? 0,
    createdAt:   d.createdAt?.toDate?.() ?? null,
    updatedAt:   d.updatedAt?.toDate?.() ?? null,
  };
}

// ── CREATE ────────────────────────────────────────────────────

/**
 * 새 공고 등록
 * @param {object} jobData  - 공고 입력 데이터
 * @param {string} employerId - 등록자 UID
 * @returns {Promise<string>} - 생성된 docId
 */
export async function createJob(jobData, employerId, employerName) {
  const ref = await addDoc(collection(db, JOBS_COL), {
    ...jobData,
    employerId,
    employerName,
    filledSlots: 0,
    viewCount:   0,
    applyCount:  0,
    status:      "open",
    createdAt:   serverTimestamp(),
    updatedAt:   serverTimestamp(),
  });
  return ref.id;
}

// ── READ (단건) ───────────────────────────────────────────────

export async function getJob(jobId) {
  const snap = await getDoc(doc(db, JOBS_COL, jobId));
  if (!snap.exists()) throw new Error("공고를 찾을 수 없습니다.");
  await updateDoc(snap.ref, { viewCount: increment(1) }); // 조회수 증가
  return toModel(snap);
}

// ── READ (목록, 페이지네이션) ─────────────────────────────────

/**
 * 공고 목록 조회 (첫 페이지)
 * @param {{ category, urgent, keyword, pageSize, lastDoc }} options
 */
export async function fetchJobs({ category, urgent, pageSize = 20, lastDoc = null } = {}) {
  let q = collection(db, JOBS_COL);
  const filters = [where("status", "==", "open"), orderBy("createdAt", "desc")];

  if (category && category !== "전체") filters.splice(1, 0, where("category", "==", category));
  if (urgent === true)                 filters.splice(1, 0, where("urgent", "==", true));

  q = query(q, ...filters, limit(pageSize));
  if (lastDoc) q = query(q, ...filters, startAfter(lastDoc), limit(pageSize));

  const snap = await getDocs(q);
  return {
    jobs:    snap.docs.map(toModel),
    lastDoc: snap.docs[snap.docs.length - 1] ?? null,
    hasMore: snap.docs.length === pageSize,
  };
}

// ── REALTIME 구독 ─────────────────────────────────────────────

/**
 * 실시간으로 공고 목록 구독
 * @param {function} onData - (jobs: Job[]) => void
 * @param {function} onError
 * @returns unsubscribe function
 */
export function subscribeJobs(onData, onError, { category, urgent } = {}) {
  const filters = [where("status", "==", "open"), orderBy("createdAt", "desc"), limit(50)];
  if (category && category !== "전체") filters.splice(1, 0, where("category", "==", category));
  if (urgent === true)                 filters.splice(1, 0, where("urgent", "==", true));

  const q = query(collection(db, JOBS_COL), ...filters);
  return onSnapshot(q, snap => onData(snap.docs.map(toModel)), onError);
}

/**
 * 특정 업체의 공고만 실시간 구독
 */
export function subscribeEmployerJobs(employerId, onData, onError) {
  const q = query(
    collection(db, JOBS_COL),
    where("employerId", "==", employerId),
    orderBy("createdAt", "desc"),
  );
  return onSnapshot(q, snap => onData(snap.docs.map(toModel)), onError);
}

// ── UPDATE ────────────────────────────────────────────────────

export async function updateJob(jobId, updates) {
  await updateDoc(doc(db, JOBS_COL, jobId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function closeJob(jobId) {
  await updateJob(jobId, { status: "closed" });
}

export async function cancelJob(jobId) {
  await updateJob(jobId, { status: "cancelled" });
}

// ── DELETE ────────────────────────────────────────────────────

export async function deleteJob(jobId) {
  await deleteDoc(doc(db, JOBS_COL, jobId));
}

// ── CLIENT-SIDE 키워드 검색 (Firestore 전문 검색 미지원 대체) ─

/**
 * 이미 가져온 jobs 배열에서 클라이언트 검색
 * 실제 서비스에서는 Algolia / Typesense 연동 권장
 */
export function searchJobs(jobs, keyword) {
  if (!keyword.trim()) return jobs;
  const kw = keyword.trim().toLowerCase();
  return jobs.filter(j =>
    j.title.toLowerCase().includes(kw) ||
    j.company.toLowerCase().includes(kw) ||
    j.location.toLowerCase().includes(kw) ||
    j.tags.some(t => t.toLowerCase().includes(kw))
  );
}

// ── 지원(Applications) ────────────────────────────────────────

/**
 * 공고 지원
 */
export async function applyToJob(jobId, userId, userProfile) {
  // 중복 지원 체크
  const dupQ = query(
    collection(db, APPLICATIONS_COL),
    where("jobId",  "==", jobId),
    where("userId", "==", userId),
  );
  const dupSnap = await getDocs(dupQ);
  if (!dupSnap.empty) throw new Error("이미 지원한 공고입니다.");

  // 지원 등록
  const appRef = await addDoc(collection(db, APPLICATIONS_COL), {
    jobId,
    userId,
    userName:  userProfile.name  ?? "",
    userPhone: userProfile.phone ?? "",
    status:    "pending",          // pending | accepted | rejected
    createdAt: serverTimestamp(),
  });

  // 공고 지원자 수 +1
  await updateDoc(doc(db, JOBS_COL, jobId), {
    applyCount: increment(1),
  });

  return appRef.id;
}

/**
 * 특정 공고의 지원자 목록 (업체용)
 */
export function subscribeApplications(jobId, onData, onError) {
  const q = query(
    collection(db, APPLICATIONS_COL),
    where("jobId", "==", jobId),
    orderBy("createdAt", "desc"),
  );
  return onSnapshot(q, snap => onData(snap.docs.map(d => ({ id: d.id, ...d.data() }))), onError);
}

/**
 * 내 지원 내역 (구직자용)
 */
export function subscribeMyApplications(userId, onData, onError) {
  const q = query(
    collection(db, APPLICATIONS_COL),
    where("userId", "==", userId),
    orderBy("createdAt", "desc"),
  );
  return onSnapshot(q, snap => onData(snap.docs.map(d => ({ id: d.id, ...d.data() }))), onError);
}

/**
 * 지원 상태 변경 (업체가 수락/거절)
 */
export async function updateApplicationStatus(appId, status, jobId) {
  await updateDoc(doc(db, APPLICATIONS_COL, appId), { status });
  if (status === "accepted") {
    await updateDoc(doc(db, JOBS_COL, jobId), { filledSlots: increment(1) });
  }
}
